import { BrowserProvider, Contract, Interface } from "ethers";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { GMT_REGIONS } from "../common/gmt_region";
import { useWallletContext } from "../hook/useWalletContext";
import STAKING_ABI from "../common/STAKING_ABI.json";
import { useWeb3ModalProvider } from "@web3modal/ethers/react";
import {
  convertFromGmtOffsetTime,
  convertToGmtOffsetTime,
} from "../common/helper";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { ShowProgress } from "../states/ShowProgress";
import STAKING_ADMIN_ABI from "../common/STAKING_ADMIN_ABI.json";
import { METAMASK_ERROR_CODE } from "../common/const";

export const ContractDateCard = () => {
  const { walletProvider } = useWeb3ModalProvider();
  const { currentAddress, theme } = useWallletContext();

  const [, setShowProgress] = useRecoilState(ShowProgress);

  const [listDate, setListDate] = useState<Date | undefined>();
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [offset, setOffset] = useState<any>({
    value: "+0",
    label: "GMT 00:00",
  });

  useEffect(() => {
    if (!currentAddress) {
      return;
    }

    getContractDate();
  }, [currentAddress]);

  const getContractDate = async () => {
    try {
      const provider = new BrowserProvider(walletProvider!);
      const signer = await provider.getSigner();

      const staking = new Contract(
        process.env.REACT_APP_STAKING_50_CONTRACT!,
        new Interface(STAKING_ABI).format(false),
        signer
      );

      const listDate = parseInt((await staking.listDate()).toString());
      const startDate = parseInt((await staking.startDate()).toString());

      if (listDate) {
        setListDate(
          convertFromGmtOffsetTime(
            new Date(listDate * 1000)
            // new Date().getTimezoneOffset() / 60
          )
        );
        setStartDate(
          convertFromGmtOffsetTime(
            new Date(startDate * 1000)
            // new Date().getTimezoneOffset() / 60
          )
        );
      } else {
        setListDate(undefined);
        setStartDate(undefined);
      }
    } catch (err) {
      console.log(err);

      setListDate(undefined);
      setStartDate(undefined);
    }
  };

  const handleUpdate = async () => {
    if (!listDate) {
      toast.warn("Please select the list date.", { theme: theme });
      return;
    }

    if (!startDate) {
      toast.warn("Please select the start date.", { theme: theme });
      return;
    }

    const listTimestamp =
      convertToGmtOffsetTime(listDate, parseInt(offset.value)).getTime() / 1000;
    const startTimestamp =
      convertToGmtOffsetTime(startDate, parseInt(offset.value)).getTime() /
      1000;

    setShowProgress(true);
    const provider = new BrowserProvider(walletProvider!);
    const signer = await provider.getSigner();

    const stakingAdmin = new Contract(
      process.env.REACT_APP_STAKING_ADMIN_CONTRACT!,
      new Interface(STAKING_ADMIN_ABI).format(false),
      signer
    );

    setShowProgress(true);

    stakingAdmin
      .setAdminDate(listTimestamp, startTimestamp)
      .then(async (tx) => {
        await tx.wait();

        setShowProgress(false);
        toast.success("Setting list date and start date has been successful.", {
          theme: theme,
        });
      })
      .catch((err) => {
        setShowProgress(false);

        if (err.code === METAMASK_ERROR_CODE.USER_REJECT) {
          return;
        }

        toast.error("Transaction reverted", { theme: theme });
      });
  };

  return (
    <>
      <div className="col-span-12 md:col-span-6 xxl:col-span-6">
        <div className="box ">
          <div className="box-header">
            <div className="sm:flex justify-between space-y-2 sm:space-y-0">
              <h5 className="box-title my-auto">Contract Date</h5>
            </div>
          </div>
          <div className="box-body">
            <div className="mb-2 grid grid-cols-12 gap-x-5">
              <div className="col-span-12 md:col-span-6 xxl:col-span-3 flex">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label my-auto text-md"
                >
                  Timezone:
                </label>
              </div>
              <div className="col-span-12 md:col-span-6 xxl:col-span-9">
                <Select
                  classNamePrefix="react-select"
                  options={GMT_REGIONS}
                  menuPlacement="bottom"
                  placeholder="Select the GMT region"
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      textAlign: "left",
                    }),
                  }}
                  value={offset}
                  onChange={(e) => setOffset(e)}
                />
              </div>
            </div>
            <div className="mb-2 grid grid-cols-12 gap-x-5">
              <div className="col-span-12 md:col-span-6 xxl:col-span-3 flex"></div>
              <div className="col-span-12 md:col-span-6 xxl:col-span-9">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label my-auto text-md text-red-400"
                >
                  ※ The actual base time of the smart contract is GMT +0.
                </label>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-x-5">
              <div className="col-span-12 md:col-span-6 xxl:col-span-3 flex">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label my-auto text-md"
                >
                  List Date:
                </label>
              </div>
              <div className="col-span-12 md:col-span-6 xxl:col-span-9">
                <div className="flex rounded-sm overflow-auto">
                  <div className="px-4 inline-flex items-center min-w-fit ltr:rounded-l-sm rtl:rounded-r-sm border ltr:border-r-0 rtl:border-l-0 border-gray-200 bg-gray-50 dark:bg-black/20 dark:border-white/10">
                    <span className="text-sm text-gray-500 dark:text-white/70">
                      <i className="ri ri-calendar-line"></i>
                    </span>
                  </div>

                  <DatePicker
                    className="ti-form-input ltr:rounded-l-none rtl:rounded-r-none focus:z-10"
                    selected={listDate}
                    onChange={(date: Date | null) =>
                      setListDate(date ?? new Date())
                    }
                    timeInputLabel="Time:"
                    dateFormat="MM/dd/yyyy HH:mm"
                    showTimeInput
                  />
                </div>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-12 gap-x-5">
              <div className="col-span-12 md:col-span-6 xxl:col-span-3 flex">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label my-auto text-md"
                >
                  Start Date:
                </label>
              </div>
              <div className="col-span-12 md:col-span-6 xxl:col-span-9">
                <div className="flex rounded-sm overflow-auto">
                  <div className="px-4 inline-flex items-center min-w-fit ltr:rounded-l-sm rtl:rounded-r-sm border ltr:border-r-0 rtl:border-l-0 border-gray-200 bg-gray-50 dark:bg-black/20 dark:border-white/10">
                    <span className="text-sm text-gray-500 dark:text-white/70">
                      <i className="ri ri-calendar-line"></i>
                    </span>
                  </div>

                  <DatePicker
                    className="ti-form-input ltr:rounded-l-none rtl:rounded-r-none focus:z-10"
                    selected={startDate}
                    onChange={(date: Date | null) =>
                      setStartDate(date ?? new Date())
                    }
                    timeInputLabel="Time:"
                    dateFormat="MM/dd/yyyy HH:mm"
                    showTimeInput
                  />
                </div>
              </div>
            </div>
            <div className="mt-2 text-center">
              <button
                type="button"
                className="ti-btn ti-btn-primary"
                onClick={handleUpdate}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
